import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { DraftingActions, kStaleDraftsTimestamp } from './slice'
import { ArtifactsActions } from '@/artifacts/store/slice'
import { getDraftsByMatterId } from '@/drafting/fetch/get-drafts-by-matter-id'
import { AgentEventsActions } from '@/agent/events/store/slice'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { selectDraftsForConversation } from './selectors'

export const draftingListenerMiddleware = createListenerMiddleware()
const startListener = draftingListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

// How long a matter's drafts stay fresh before a non-forced refresh refetches.
const kMatterDraftsCacheDurationMs = 60 * 1000

/**
 * Refresh Matter Drafts Listener
 *
 * Loads a matter's drafts into the store.
 */
startListener({
  actionCreator: DraftingActions.refreshMatterDrafts,
  effect: async (action, listenerApi) => {
    const { matterId, forceRefresh } = action.payload
    const state = listenerApi.getState().draftingState

    // Skip if a fetch is already in flight, or the cache is still fresh and this isn't forced
    if (state.loadingByMatterId[matterId]) return
    const lastFetch = state.lastRefreshByMatterId[matterId] ?? null
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > kMatterDraftsCacheDurationMs
    if (!cacheExpired && !forceRefresh) return

    try {
      listenerApi.dispatch(DraftingActions.setLoadingForMatter({ matterId, loading: true }))
      listenerApi.dispatch(DraftingActions.setErrorForMatter({ matterId, error: false }))

      const drafts = await getDraftsByMatterId(matterId)

      // Records go to the shared artifacts store
      listenerApi.dispatch(ArtifactsActions.upsertArtifactVersions(drafts.map((d) => d.artifact_version)))

      // The drafting slice keeps the ids + conversation relation
      listenerApi.dispatch(
        DraftingActions.setDraftArtifactVersionConversationIds(Object.fromEntries(drafts.map((d) => [d.artifact_version.id, d.conversation_id])))
      )
      listenerApi.dispatch(DraftingActions.setDraftArtifactVersionIdsForMatter({ matterId, artifactVersionIds: drafts.map((d) => d.artifact_version.id) }))
    } catch {
      listenerApi.dispatch(DraftingActions.setErrorForMatter({ matterId, error: true }))
    } finally {
      listenerApi.dispatch(DraftingActions.setLoadingForMatter({ matterId, loading: false }))

      // If an invalidation landed while this fetch was in flight, the result may predate it, so refetch
      // rather than stamping the cache fresh (which would bury the invalidation until the next visit).
      // The `lastFetch` guard prevents a loop: the forced refetch this triggers starts already-stale,
      // so its own completion takes the stamp-fresh branch instead of re-triggering.
      const invalidatedMidFetch =
        listenerApi.getState().draftingState.lastRefreshByMatterId[matterId] === kStaleDraftsTimestamp && lastFetch !== kStaleDraftsTimestamp
      if (invalidatedMidFetch) {
        listenerApi.dispatch(DraftingActions.refreshMatterDrafts({ matterId, forceRefresh: true }))
      } else {
        listenerApi.dispatch(DraftingActions.setLastRefreshForMatter({ matterId, timestamp: Date.now() }))
      }
    }
  },
})

/**
 * Invalidate Drafts On New Version Listener
 *
 * A new or regenerated artifact version adds a draft to the matter, so invalidate the matter's drafts cache.
 */
startListener({
  actionCreator: AgentEventsActions.updateArtifactVersionId,
  effect: (action, listenerApi) => {
    const matterId = selectAgentConversationMatterId(listenerApi.getState(), { conversationId: action.payload.conversationId })
    if (!matterId) return

    listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})

/**
 * Invalidate Drafts On Conversation Delete Listener
 *
 * A deleted conversation should no longer contribute drafts (the matter-drafts fetch excludes hidden
 * conversations), so invalidate the matter's drafts cache.
 */
startListener({
  actionCreator: AgentConversationsActions.deleteConversation,
  effect: (action, listenerApi) => {
    const { conversationId } = action.payload
    // getOriginalState, not getState: deleteConversation removes the conversation before this runs, so
    // neither its matter nor its drafts are resolvable from the current state.
    const originalState = listenerApi.getOriginalState()

    const matterId = selectAgentConversationMatterId(originalState, { conversationId })
    if (!matterId) return

    // Skip if the deleted conversation had no drafts
    if (selectDraftsForConversation(originalState, { matterId, conversationId }).length === 0) return

    listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})

/**
 * Invalidate Drafts On Conversation Move Listener
 *
 * Moving a conversation to a different matter takes its drafts with it (the matter-drafts fetch keys off
 * the conversation's matter), so invalidate both the matter it left and the one it joined.
 */
startListener({
  actionCreator: AgentConversationsActions.updateConversationMatter,
  effect: (action, listenerApi) => {
    const { conversationId, matterId: newMatterId } = action.payload
    // getOriginalState, not getState: updateConversationMatter has already moved the conversation, so the
    // matter it left is only resolvable from the state before the action.
    const originalState = listenerApi.getOriginalState()

    const oldMatterId = selectAgentConversationMatterId(originalState, { conversationId })

    // Skip if the moved conversation had no drafts; neither matter's drafts change.
    if (!oldMatterId || selectDraftsForConversation(originalState, { matterId: oldMatterId, conversationId }).length === 0) return

    listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId: oldMatterId }))
    if (newMatterId) listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId: newMatterId }))
  },
})
